import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import LoadingState from '../../components/ui/LoadingState';
import ScenarioCard from './components/ScenarioCard';
import ResponseInput from './components/ResponseInput';


import ActionButtons from './components/ActionButtons';
import HintPanel from './components/HintPanel';
import { LumoCoachingPanel } from '../../components/LumoCoachingPanel';
import TechniqueDetectionBadge from '../../components/TechniqueDetectionBadge';
import { drillsService } from '../../services/drillsService';
import { profileService } from '../../services/profileService';
import { supabase } from '../../lib/supabase';


import { lumoEdgeFunctionService } from '../../services/lumoEdgeFunctionService';
import lumoStrategyService from '../../services/lumoStrategyService';
import lumoTechniquesService from '../../services/lumoTechniquesService';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

const DailyDrill = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const { tenantId } = useTenant();
  const drillIdParam = searchParams.get('drillId');
  const [isLoading, setIsLoading] = useState(true);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [userStreak, setUserStreak] = useState(0);
  const [drill, setDrill] = useState(null);
  const [userDrillId, setUserDrillId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [xpReward, setXpReward] = useState(null);
  const [startTime] = useState(Date.now());
  const [errorMessage, setErrorMessage] = useState('');
  const [responseText, setResponseText] = useState('');
  const [loadError, setLoadError] = useState('');
  const [hints, setHints] = useState([]);
  const [lumoScores, setLumoScores] = useState(null);
  const [lumoFeedback, setLumoFeedback] = useState([]);
  const [improvementDelta, setImprovementDelta] = useState(null);
  const [detectedPhrases, setDetectedPhrases] = useState([]);
  const [detectedTechnique, setDetectedTechnique] = useState(null);
  const [techniqueScore, setTechniqueScore] = useState(null);

  // Mock scenario data - used as fallback if no drill is fetched
  // Using a valid UUID format to match database schema
  const mockScenario = {
    id: "00000000-0000-0000-0000-000000000001",
    title: "Handling Price Objection in SaaS Sales",
    difficulty: "intermediate",
    estimatedTime: 8,
    industry: "Technology",
    customerProfile: "Sarah Chen, VP of Operations at a mid-sized logistics company with 200 employees. She's tech-savvy, data-driven, and has been researching solutions for 3 months. Budget-conscious but values ROI.",
    situation: "You're on a discovery call with Sarah. She's interested in your warehouse management software but just said: 'Your pricing is 40% higher than your competitor. I like your features, but I need to justify this cost to my CFO. Why should we pay more?'",
    challenge: "Address the price objection while reinforcing value, differentiating from competitors, and moving toward a close. Demonstrate confidence without being defensive.",
    hints: [
      "Start by acknowledging her concern and showing you understand the importance of ROI to her CFO.",
      "Reframe the conversation from price to total cost of ownership and long-term value.",
      "Use a specific example or metric that demonstrates how your solution saves money or increases efficiency.",
      "Ask a question that helps her articulate the cost of NOT solving the problem.",
      "End with a clear next step that moves toward closing, such as a customized ROI analysis."
    ]
  };

  // Fetch today's drill on component mount
  useEffect(() => {
    const fetchTodaysDrill = async () => {
      try {
        setIsLoading(true);
        setLoadError('');

        // Fetch user profile to get streak
        const profile = await profileService?.getUserProfile();
        setUserStreak(profile?.streakDays || 0);

        let drillToShow;
        
        // If drillId is passed as query param, fetch that specific drill
        if (drillIdParam) {
          drillToShow = await drillsService?.getById(drillIdParam, tenantId);
          if (!drillToShow) {
            console.warn('Could not fetch drill with ID:', drillIdParam);
            // Fall back to today's drill
            drillToShow = await drillsService?.getTodaysDrillByLevel(
              profile?.level || 1,
              profile,
              tenantId
            );
          }
        } else {
          // Fetch today's drill - same drill shown across all pages, based on user level
          drillToShow = await drillsService?.getTodaysDrillByLevel(
            profile?.level || 1,
            profile,
            tenantId
          );
        }
        
        if (drillToShow) {
          console.log('Drill loaded:', drillToShow.title, 'Hints:', drillToShow.hints);
          setDrill(drillToShow);
          // Set hints from drill data, or use empty array if none exist
          setHints(drillToShow.hints || []);
        } else {
          // No drills found - use mock data
          console.warn('No drills found in database, using mock data');
          setDrill(mockScenario);
          setHints(mockScenario.hints || []);
        }
      } catch (error) {
        console.error('Error fetching drill:', error);
        setLoadError(`Failed to load drill: ${error?.message}`);
        // Fall back to mock data if fetch fails
        setDrill(mockScenario);
        setHints(mockScenario.hints || []);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.id) {
      fetchTodaysDrill();
    }
    window.scrollTo(0, 0);
  }, [user?.id, drillIdParam]);

  const handleSubmitResponse = async (response) => {
    // Validate response is not empty
    if (!response || response?.trim()?.length === 0) {
      setErrorMessage('Please enter your response before submitting.');
      return;
    }

    // Check if using mock scenario (no drills in database)
    if (drill?.id === "00000000-0000-0000-0000-000000000001") {
      setErrorMessage('No drills found in database. Please contact your administrator to seed drills.');
      return;
    }

    try {
      setIsLoading(true);
      setIsEvaluating(true);
      setErrorMessage('');
      setResponseText(response);
      
      // Calculate time taken
      const endTime = Date.now();
      const timeTakenSeconds = Math.floor((endTime - startTime) / 1000);

      // First, create the user_drills record to get the ID
      const { data: userDrill, error: createError } = await supabase
        ?.from('user_drills')
        ?.insert({
          user_id: user?.id,
          drill_id: drill?.id,
          response_text: response,
          time_taken_seconds: timeTakenSeconds,
          completed_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (createError) throw createError;

      setUserDrillId(userDrill?.id);

      // Call the Lumo Edge Function with the user_drill_id
      const evaluationResult = await lumoEdgeFunctionService?.evaluateDrillWithLumo({
        userId: user?.id,
        userDrillId: userDrill?.id,
        drillId: drill?.id,
        userResponseText: response
      });

      if (!evaluationResult?.success) {
        const errorMsg = evaluationResult?.error?.message || 'Lumo evaluation failed';
        const helpText = evaluationResult?.error?.helpText || '';
        throw new Error(`${errorMsg}\n\n${helpText}`);
      }

      const lumoData = evaluationResult?.data;

      // Update the user_drills record with Lumo results
      await lumoEdgeFunctionService?.updateDrillWithLumoResults(
        userDrill?.id,
        lumoData
      );

      // Update user profile with XP rewards based on overall score
      const updatedProfile = await profileService?.updateProfileAfterDrill(
        lumoData?.overallScore || 0,
        {
          contentDifficulty: drill?.difficulty_level || 1,
          userSkillLevel: profile?.skill_level === 'advanced' ? 5 : profile?.skill_level === 'intermediate' ? 3 : 1
        }
      );

      // Calculate Lumo Strategy scores
      const avoidPhrases = lumoStrategyService.getAvoidPhrases(profile?.preferred_persona || 'CFO');
      const salesyDetected = avoidPhrases.filter(phrase => 
        response.toLowerCase().includes(phrase.toLowerCase())
      );
      
      const questionCount = (response.match(/\?/g) || []).length;
      const statementCount = (response.match(/\./g) || []).length;
      
      const sessionData = {
        responseLength: response.length,
        questionCount,
        statementCount,
        salesyPhraseCount: salesyDetected.length,
        pauseAfterObjection: 0, // Not applicable for written drills
        pivotSmoothness: 0, // Single objection drill
        outcomeNeediness: lumoData?.confidenceScore < 70 ? 0.3 : 0.1
      };
      
      const calculatedLumoScores = lumoStrategyService.calculateLumoScores(sessionData);
      setLumoScores(calculatedLumoScores);
      setDetectedPhrases(salesyDetected);
      
      // Detect which Lumo technique was used
      const techniqueDetection = lumoTechniquesService.detectTechniqueUsed(response, drill?.prompt_text || '');
      setDetectedTechnique(techniqueDetection);
      
      // If technique detected, score the execution
      if (techniqueDetection?.techniqueSlug) {
        const technique = await lumoTechniquesService.getTechniqueBySlug(techniqueDetection.techniqueSlug);
        if (technique) {
          const executionScore = lumoTechniquesService.scoreTechniqueExecution(
            technique, 
            response, 
            { timeToExecute: timeTakenSeconds }
          );
          setTechniqueScore(executionScore);
          
          // Track technique application
          try {
            await lumoTechniquesService.trackTechniqueApplication({
              userId: user.id,
              techniqueId: technique.id,
              sessionId: userDrill.id,
              sessionType: 'daily_drill',
              objectionFaced: drill?.prompt_text || '',
              appliedCorrectly: executionScore >= 0.7,
              executionScore: executionScore,
              timeToExecute: timeTakenSeconds,
              outcome: lumoData?.overallScore >= 80 ? 'overcome' : lumoData?.overallScore >= 60 ? 'partial_overcome' : 'failed',
              whatWorked: executionScore >= 0.7 ? 'Good technique execution' : '',
              whatNeedsWork: executionScore < 0.7 ? 'Technique needs refinement' : ''
            });
          } catch (trackError) {
            console.error('⚠️ Technique tracking failed:', trackError);
          }
        }
      }
      
      // Get previous drill score for comparison
      const { data: previousDrills } = await supabase
        .from('user_drills')
        .select('overall_score, created_at')
        .eq('user_id', user.id)
        .eq('drill_id', drill.id)
        .order('created_at', { ascending: false })
        .limit(2);
      
      const previousScore = previousDrills?.length > 1 ? previousDrills[1].overall_score : null;
      
      // Track objection stack with Lumo metrics
      let lumoResult = null;
      try {
        lumoResult = await lumoStrategyService.trackObjectionStackWithLumo({
          userId: user.id,
          sessionId: userDrill.id,
          sessionType: 'daily_drill',
          objectionSequence: [drill.id],
          successfulPivots: 0,
          failedPivots: 0,
          overallHandlingScore: lumoData?.overallScore || 0,
          timeToResolve: timeTakenSeconds,
          resolutionOutcome: lumoData?.overallScore >= 80 ? 'overcome' : 'partial_overcome',
          lumoScores: calculatedLumoScores,
          previousSessionScore: previousScore
        });
        
        setLumoFeedback(lumoResult.feedback || []);
        setImprovementDelta(lumoResult.improvementDelta);
      } catch (lumoError) {
        console.error('⚠️ Lumo tracking failed:', lumoError);
      }

      // Store feedback for display
      setFeedback({
        overallScore: lumoData?.overallScore,
        clarityScore: lumoData?.clarityScore,
        confidenceScore: lumoData?.confidenceScore,
        controlScore: lumoData?.controlScore,
        closeScore: lumoData?.closeScore,
        feedbackText: lumoData?.feedbackText,
        suggestedScript: lumoData?.suggestedScript
      });

      // Track outcome for continuous learning (if response unit was used)
      try {
        if (drill?.recommendedResponseId) {
          await outcomeTracking.recordDrillOutcome({
            responseUnitId: drill.recommendedResponseId,
            userId: user?.id,
            drillId: drill?.id,
            scores: {
              clarity: lumoData?.clarityScore || 0,
              confidence: lumoData?.confidenceScore || 0,
              control: lumoData?.controlScore || 0,
              close: lumoData?.closeScore || 0,
              overall: lumoData?.overallScore || 0
            },
            userResponse: response,
            timeTakenSeconds: timeTakenSeconds,
            context: {
              persona: profile?.preferred_persona || 'CONSULTATIVE',
              stage: 'practice',
              industry: profile?.industry || 'general',
              drillType: drill?.type || 'general',
              difficulty: drill?.difficulty_level
            }
          });
        }
      } catch (outcomeError) {
        console.error('⚠️ Outcome tracking failed:', outcomeError);
      }

      // Update gamification: streak and missions
      try {
        await gamificationService.updateStreak('drill');
        
        const missions = await gamificationService.getTodaysMissions();
        for (const mission of missions) {
          if ((mission.mission_type === 'drill' || mission.mission_type === 'combo') && !mission.isCompleted) {
            const currentProgress = mission.progress || {};
            const drillsCompleted = (currentProgress.drills_completed || 0) + 1;
            const scores = currentProgress.drill_scores || [];
            scores.push(lumoData?.overallScore || 0);
            const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
            
            await gamificationService.updateMissionProgress(mission.progressId, {
              drills_completed: drillsCompleted,
              drill_scores: scores,
              min_avg_score: Math.round(avgScore),
              drills_with_90_plus: lumoData?.overallScore >= 90 ? (currentProgress.drills_with_90_plus || 0) + 1 : (currentProgress.drills_with_90_plus || 0),
              perfect_drills: lumoData?.overallScore === 100 ? (currentProgress.perfect_drills || 0) + 1 : (currentProgress.perfect_drills || 0)
            });
          }
        }
      } catch (gamError) {
        console.error('⚠️ Gamification update failed:', gamError);
      }

      setXpReward({
        totalXP: updatedProfile?.xpEarned,
        baseXP: updatedProfile?.baseXP,
        bonusXP: updatedProfile?.bonusXP,
        newLevel: updatedProfile?.level,
        leveledUp: updatedProfile?.leveledUp,
        newStreak: updatedProfile?.streakDays
      });
      
      // Update streak display
      setUserStreak(updatedProfile?.streakDays || 0);
      
      setHasSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting response:', error);
      
      // Enhanced error message with detailed information
      let userFriendlyMessage = 'Lumo had trouble evaluating that one. ';
      
      if (error?.message?.includes('Edge Function')) {
        userFriendlyMessage = error?.message;
      } else if (error?.message?.includes('not been deployed')) {
        userFriendlyMessage = error?.message;
      } else if (error?.message?.includes('Unable to reach')) {
        userFriendlyMessage = error?.message;
      } else {
        userFriendlyMessage += error?.message || 'Please try again in a moment.';
      }
      
      setErrorMessage(userFriendlyMessage);
      
      // Don't set hasSubmitted if there was an error
      setHasSubmitted(false);
    } finally {
      setIsLoading(false);
      setIsEvaluating(false);
    }
  };

  const handleRetry = () => {
    setHasSubmitted(false);
    setFeedback(null);
    setXpReward(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading && !drill) {
    return <LoadingState message="Loading today's drill..." />;
  }

  if (isLoading && hasSubmitted) {
    return <LoadingState message="Lumo is analyzing your response and preparing personalized feedback..." />;
  }

  if (!drill) {
    return (
      <>
        <NavigationBar userStreak={userStreak} />
        <div className="main-content bg-background">
          <div className="max-w-4xl mx-auto text-center py-12">
            <h2 className="text-2xl font-bold text-foreground mb-4">No drills available</h2>
            <p className="text-muted-foreground mb-6">Please check back later or contact support.</p>
            <button 
              onClick={() => navigate('/app/dashboard')}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Daily Drill - EME</title>
        <meta name="description" content="Practice your sales skills with AI-powered daily drills and get instant feedback on your performance." />
      </Helmet>
      <NavigationBar userStreak={userStreak} />
      {profile?.inactivityWarning && (
        <div className="mx-auto max-w-4xl mt-4 p-4 border-2 border-yellow-200 bg-yellow-50 rounded-lg flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600" />
          <div>
            <p className="text-sm text-yellow-800">
              Run a drill today to avoid losing <span className="font-semibold">{profile.inactivityWarning.xpAtRisk} XP</span>
              {profile.inactivityWarning.daysUntilDecay === 0 ? ' today' : ` in ${profile.inactivityWarning.daysUntilDecay} day(s)`}.
              Next decay date: <span className="font-mono">{profile.inactivityWarning.nextDecayDate}</span>.
            </p>
          </div>
        </div>
      )}
      <div className="main-content bg-background">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              Today's Drill
            </h1>
            <p className="text-muted-foreground">
              {hasSubmitted 
                ? "Here's your performance analysis and coaching feedback from Lumo"
                : "Practice this scenario and get AI-powered feedback to improve your skills"
              }
            </p>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <LoadingState message="Loading today's drill..." />
          ) : (
            <>
              {/* Load Error Display */}
              {loadError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-yellow-600 text-xl">ℹ️</span>
                    <div>
                      <h3 className="font-semibold text-yellow-800 mb-1">Note</h3>
                      <p className="text-yellow-700 text-sm">{loadError}</p>
                      <p className="text-yellow-700 text-xs mt-1">Using sample drill for now.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message Display */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-red-500 text-xl">⚠️</span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-800 mb-1">Error</h3>
                      <p className="text-red-700 text-sm">{errorMessage}</p>
                      <button
                        onClick={() => navigator?.clipboard?.writeText(errorMessage)}
                        className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                      >
                        Copy error details
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!hasSubmitted ? (
                <>
                  {/* Scenario Card */}
                  {drill && (
                    <>
                      <ScenarioCard scenario={{
                        id: drill?.id,
                        title: drill?.title,
                        difficulty: drill?.difficultyLevel === 1 ? "Beginner" : drill?.difficultyLevel === 2 ? "Intermediate" : drill?.difficultyLevel === 3 ? "Advanced" : "Intermediate",
                        estimatedTime: drill?.estimatedTime || 8,
                        industry: drill?.industry,
                        situation: drill?.situation || drill?.promptText
                      }} />

                      {/* Hint Panel */}
                      <HintPanel hints={hints} />

                      {/* Response Input with loading state */}
                      <ResponseInput 
                        onSubmit={handleSubmitResponse}
                        isLoading={isEvaluating}
                        minWords={50}
                        maxWords={200}
                        buttonText={isEvaluating ? "Lumo is reviewing…" : "Submit to Lumo"}
                      />
                    </>
                  )}
                </>
              ) : (
            <>
              {/* XP Reward Display */}
              {xpReward && (
                <div className="bg-gradient-to-r from-accent/20 to-primary/20 border border-accent/30 rounded-2xl p-6">
                  <div className="text-center space-y-3">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-4xl">🎉</span>
                      <h3 className="text-2xl font-bold text-foreground">
                        Lumo's Feedback is Ready!
                      </h3>
                    </div>
                    <div className="flex items-center justify-center gap-6 text-lg">
                      <div>
                        <span className="text-accent font-bold">+{xpReward?.totalXP} XP</span>
                        <span className="text-muted-foreground text-sm ml-2">
                          ({xpReward?.baseXP} base{xpReward?.bonusXP > 0 ? ` + ${xpReward?.bonusXP} bonus` : ''})
                        </span>
                      </div>
                      {xpReward?.leveledUp && (
                        <div className="text-primary font-bold">
                          Level {xpReward?.newLevel}!
                        </div>
                      )}
                      <div className="text-foreground">
                        🔥 {xpReward?.newStreak} day streak!
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Score Card with Lumo scores */}
              {feedback && (
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-foreground mb-4">Performance Scores</h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {/* Overall Score */}
                    <div className="text-center p-4 bg-primary/10 rounded-lg">
                      <div className="text-3xl font-bold text-primary mb-1">
                        {feedback?.overallScore || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Overall</div>
                    </div>
                    
                    {/* Sub-scores */}
                    <div className="text-center p-4 bg-accent/10 rounded-lg">
                      <div className="text-2xl font-bold text-accent mb-1">
                        {feedback?.clarityScore || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Clarity</div>
                    </div>
                    
                    <div className="text-center p-4 bg-accent/10 rounded-lg">
                      <div className="text-2xl font-bold text-accent mb-1">
                        {feedback?.confidenceScore || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Confidence</div>
                    </div>
                    
                    <div className="text-center p-4 bg-accent/10 rounded-lg">
                      <div className="text-2xl font-bold text-accent mb-1">
                        {feedback?.controlScore || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Control</div>
                    </div>
                    
                    <div className="text-center p-4 bg-accent/10 rounded-lg">
                      <div className="text-2xl font-bold text-accent mb-1">
                        {feedback?.closeScore || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Close</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Feedback Section */}
              {feedback?.feedbackText && (
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-foreground mb-4">
                    Lumo's Detailed Feedback
                  </h3>
                  <div className="prose prose-sm max-w-none text-muted-foreground">
                    <p className="whitespace-pre-wrap">{feedback?.feedbackText}</p>
                  </div>
                </div>
              )}

              {/* Suggested Script */}
              {feedback?.suggestedScript && (
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-foreground mb-4">
                    Suggested Script from Lumo
                  </h3>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-foreground whitespace-pre-wrap">
                      {feedback?.suggestedScript}
                    </p>
                  </div>
                </div>
              )}

              {/* Lumo Strategy Coaching Panel */}
              {lumoScores && (
                <LumoCoachingPanel
                  scores={lumoScores}
                  feedback={lumoFeedback}
                  currentScore={feedback?.overallScore || 0}
                  previousScore={improvementDelta !== null ? Math.round(feedback?.overallScore / (1 + improvementDelta/100)) : null}
                  improvementDelta={improvementDelta}
                  detectedPhrases={detectedPhrases}
                />
              )}

              {/* Technique Detection Badge */}
              {detectedTechnique && (
                <TechniqueDetectionBadge
                  detection={detectedTechnique}
                  executionScore={techniqueScore}
                />
              )}

              {/* Action Buttons */}
              <ActionButtons 
                onRetry={handleRetry}
                showRetry={true}
                showContinue={true}
              />
            </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DailyDrill;